import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, LayoutChangeEvent, Platform, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { captureRef } from 'react-native-view-shot';
import theme from '@/config/theme';
import { traceImage } from '@/utils/debugImageTrace';
import { useRetainedLocalMediaPreview } from '@/hooks/useRetainedLocalMediaPreview';
import { ChecklistImageLoadNotice } from './ChecklistImageLoadNotice';
import {
  isChecklistMarkTap,
  farthestChecklistMarkGesturePoint,
  normalizeChecklistMarkPoint,
  type ChecklistMarkGesturePoint,
  type ChecklistMarkPoint,
} from '@/services/checklist/checklistMarking';

export interface InlineMarkPadHandle {
  capture: () => Promise<string>;
}

interface MarkGesture {
  sourceUri: string;
  sourceGeneration: number;
  startScreen: ChecklistMarkGesturePoint;
  furthestScreen: ChecklistMarkGesturePoint;
}

interface InlineMarkPadProps {
  fieldId: string;
  sourceUri: string | null;
  previewScopeKey?: string;
  points: ChecklistMarkPoint[];
  disabled?: boolean;
  onPointsChange: (points: ChecklistMarkPoint[]) => void;
  onInteraction: (kind: 'tap' | 'drag') => boolean;
  onDrawingEvent?: (phase: 'drawing_started' | 'drawing_cleared' | 'drawing_empty' | 'drawing_cancelled') => void;
}

const MARK_EXPORT_MAX_WIDTH = 1280;
const MARK_CORS_PROBE_TIMEOUT_MS = 15_000;

const waitForCorsReadableImage = (uri: string) => new Promise<void>((resolve, reject) => {
  const probe = new window.Image();
  let settled = false;
  const timeout = setTimeout(() => finish(new Error('A imagem de fundo demorou para permitir a captura.')), MARK_CORS_PROBE_TIMEOUT_MS);
  const finish = (error?: Error) => {
    if (settled) return;
    settled = true;
    clearTimeout(timeout);
    probe.onload = null;
    probe.onerror = null;
    if (error) {
      probe.src = '';
      reject(error);
    } else {
      resolve();
    }
  };
  probe.crossOrigin = 'anonymous';
  probe.onload = () => finish();
  probe.onerror = () => finish(new Error('A imagem de fundo não permite captura no navegador.'));
  probe.src = uri;
});

const InlineMarkPad = React.forwardRef<InlineMarkPadHandle, InlineMarkPadProps>(({
  fieldId,
  sourceUri,
  previewScopeKey = fieldId,
  points,
  disabled = false,
  onPointsChange,
  onInteraction,
  onDrawingEvent,
}, ref) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const stageRef = useRef<View>(null);
  const sourceUriRef = useRef(sourceUri);
  const retainedPreview = useRetainedLocalMediaPreview(sourceUri, previewScopeKey);
  const sourceGeneration = retainedPreview.generation;
  const sourceGenerationRef = useRef(sourceGeneration);
  const acceptedImageStateRef = useRef<{ uri: string; generation: number; status: 'ready' | 'failed' } | null>(null);
  sourceUriRef.current = sourceUri;
  sourceGenerationRef.current = sourceGeneration;
  const readinessTraceRef = useRef<{ generation: number; startedAt: number; retainedPreviewAtStart: boolean; emitted: boolean } | null>(null);
  if (sourceUri && readinessTraceRef.current?.generation !== sourceGeneration) {
    readinessTraceRef.current = { generation: sourceGeneration, startedAt: Date.now(), retainedPreviewAtStart: retainedPreview.pending, emitted: false };
  }
  const gestureStartRef = useRef<MarkGesture | null>(null);
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const captureLockRef = useRef(false);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [imageReadyGeneration, setImageReadyGeneration] = useState<number | null>(null);
  const imageReady = imageReadyGeneration === sourceGeneration;
  const [stageWidth, setStageWidth] = useState(0);
  const [imageFailed, setImageFailed] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const aspectRatio = useMemo(() => {
    if (!imageSize || imageSize.width <= 0 || imageSize.height <= 0) return 4 / 3;
    return imageSize.width / imageSize.height;
  }, [imageSize]);
  const stageHeight = stageWidth > 0 ? stageWidth / aspectRatio : 0;

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setStageWidth(event.nativeEvent.layout.width);
  }, []);

  const pointFromEvent = useCallback((event: any) => {
    const native = event.nativeEvent ?? event;
    const touch = native.changedTouches?.[0] ?? native.touches?.[0] ?? native;
    const stage = stageRef.current as unknown as { getBoundingClientRect?: () => { left: number; top: number } } | null;
    const rect = Platform.OS === 'web' ? stage?.getBoundingClientRect?.() : undefined;
    const clientX = Number(touch.clientX ?? touch.pageX ?? touch.locationX ?? 0);
    const clientY = Number(touch.clientY ?? touch.pageY ?? touch.locationY ?? 0);
    return {
      local: rect ? { x: clientX - rect.left, y: clientY - rect.top } : {
        x: Number(touch.locationX ?? Number.NaN),
        y: Number(touch.locationY ?? Number.NaN),
      },
      screen: { x: clientX, y: clientY },
    };
  }, []);

  const startGesture = useCallback((event: any) => {
    if (disabled || capturing || captureLockRef.current || !sourceUri || !imageReady || imageFailed || !imageSize || stageWidth <= 0 || stageHeight <= 0) return;
    const point = pointFromEvent(event);
    gestureStartRef.current = {
      sourceUri,
      sourceGeneration,
      startScreen: point.screen,
      furthestScreen: point.screen,
    };
  }, [capturing, disabled, imageReady, imageFailed, imageSize, pointFromEvent, sourceGeneration, sourceUri, stageHeight, stageWidth]);

  const moveGesture = useCallback((event: any) => {
    const gesture = gestureStartRef.current;
    if (!gesture) return;
    const point = pointFromEvent(event).screen;
    gesture.furthestScreen = farthestChecklistMarkGesturePoint(gesture.startScreen, gesture.furthestScreen, point);
  }, [pointFromEvent]);

  const finishGesture = useCallback((event: any, cancelled = false) => {
    const gesture = gestureStartRef.current;
    gestureStartRef.current = null;
    if (!gesture || gesture.sourceUri !== sourceUriRef.current || gesture.sourceGeneration !== sourceGenerationRef.current || disabled || capturing || captureLockRef.current || !sourceUri || !imageSize || !stageWidth || !stageHeight) return;
    const point = pointFromEvent(event);
    gesture.furthestScreen = farthestChecklistMarkGesturePoint(gesture.startScreen, gesture.furthestScreen, point.screen);
    const isTap = isChecklistMarkTap(gesture.startScreen, gesture.furthestScreen);
    if (cancelled || !isTap) {
      if (!isTap) {
        onDrawingEvent?.(points.length ? 'drawing_started' : 'drawing_empty');
      }
      return;
    }
    const localPoint = point.local;
    if (localPoint.x < 0 || localPoint.y < 0 || localPoint.x > stageWidth || localPoint.y > stageHeight) {
      return;
    }
    if (!onInteraction('tap')) return;
    if (!Number.isFinite(localPoint.x) || !Number.isFinite(localPoint.y) || disabled || captureLockRef.current) return;
    const normalized = normalizeChecklistMarkPoint(localPoint.x, localPoint.y, stageWidth, stageHeight);
    if (!normalized) return;
    const currentPoints = pointsRef.current;
    if (currentPoints.length === 0) onDrawingEvent?.('drawing_started');
    const nextPoints = [...currentPoints, normalized];
    pointsRef.current = nextPoints;
    onPointsChange(nextPoints);
  }, [capturing, disabled, imageSize, onDrawingEvent, onInteraction, onPointsChange, pointFromEvent, sourceUri, stageHeight, stageWidth]);

  const traceLoad = useCallback((event: string, generation: number, details: Record<string, unknown> = {}) => {
    traceImage(event, { fieldId, generation, previewScopeKey,
      sourceKind: !sourceUri ? 'none' : /^https?:/i.test(sourceUri) ? 'remote' : 'local', ...details });
  }, [fieldId, previewScopeKey, sourceUri]);

  React.useEffect(() => {
    if (sourceUriRef.current !== sourceUri || sourceGenerationRef.current !== sourceGeneration) return;
    gestureStartRef.current = null;
    // O carregamento nativo pode confirmar esta geração antes deste efeito.
    const accepted = acceptedImageStateRef.current;
    if (accepted?.uri !== sourceUri || accepted.generation !== sourceGeneration) {
      if (!retainedPreview.pending) setImageSize(null);
      setImageFailed(false);
    }
    traceLoad('mark_image_start', sourceGeneration, { retained: retainedPreview.pending });
  }, [sourceUri, sourceGeneration, traceLoad]);

  const acceptImageCallback = useCallback((uri: string, generation: number, phase: string) => {
    if (sourceUriRef.current === uri && sourceGenerationRef.current === generation) return true;
    traceLoad('mark_image_stale', generation, { phase });
    return false;
  }, [traceLoad]);

  const applyImageSize = useCallback((uri: string, generation: number, width: number, height: number) => {
    if (!acceptImageCallback(uri, generation, 'dimensions')) return;
    const valid = width > 0 && height > 0;
    acceptedImageStateRef.current = { uri, generation, status: valid ? 'ready' : 'failed' };
    if (valid) {
      const readiness = readinessTraceRef.current;
      if (readiness?.generation === generation && !readiness.emitted) {
        readiness.emitted = true;
        traceLoad('mark_image_ready', generation, {
          durationMs: Math.max(0, Date.now() - readiness.startedAt),
          retainedPreviewAtStart: readiness.retainedPreviewAtStart,
          waitingWithoutRetainedPreview: !readiness.retainedPreviewAtStart,
        });
      }
      setImageSize(current => current?.width === width && current.height === height ? current : { width, height });
      setImageReadyGeneration(generation);
      setImageFailed(false);
      return;
    }
    traceLoad('mark_image_dimensions_invalid', generation, { width, height, valid });
    setImageFailed(true);
    setImageSize(null);
  }, [acceptImageCallback, traceLoad]);

  const resolveImageSize = useCallback((uri: string, generation: number) => {
    if (!acceptImageCallback(uri, generation, 'getSize')) return;
    Image.getSize(
      uri,
      (width, height) => applyImageSize(uri, generation, width, height),
      () => {
        if (!acceptImageCallback(uri, generation, 'getSizeError')) return;
        traceLoad('mark_image_get_size_error', generation);
        applyImageSize(uri, generation, 0, 0);
      },
    );
  }, [acceptImageCallback, applyImageSize, traceLoad]);

  React.useImperativeHandle(ref, () => ({
    capture: async () => {
      if (!sourceUri || !imageReady || imageFailed || !imageSize || stageWidth <= 0 || stageHeight <= 0 || points.length === 0) {
        throw new Error('A imagem ainda não está pronta para salvar a marcação.');
      }
      if (captureLockRef.current) throw new Error('A captura da marcação já está em andamento.');
      captureLockRef.current = true;
      setCapturing(true);
      try {
        if (Platform.OS === 'web' && /^https?:/i.test(sourceUri) && typeof window !== 'undefined') {
          const sourceOrigin = new URL(sourceUri, window.location.href).origin;
          if (sourceOrigin !== window.location.origin) {
            await waitForCorsReadableImage(sourceUri);
          }
        }
        if (sourceUriRef.current !== sourceUri || sourceGenerationRef.current !== sourceGeneration) {
          throw new Error('A imagem de fundo mudou antes da captura.');
        }
        await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
        if (sourceUriRef.current !== sourceUri || sourceGenerationRef.current !== sourceGeneration) {
          throw new Error('A imagem de fundo mudou antes da captura.');
        }
        const outputWidth = Math.min(MARK_EXPORT_MAX_WIDTH, Math.max(1, Math.round(stageWidth * 2)));
        const outputHeight = Math.max(1, Math.round(outputWidth / aspectRatio));
        const result = await captureRef(stageRef, {
          format: 'jpg',
          quality: 0.75,
          result: 'data-uri',
          width: outputWidth,
          height: outputHeight,
        });
        if (sourceUriRef.current !== sourceUri || sourceGenerationRef.current !== sourceGeneration) {
          throw new Error('A imagem de fundo mudou durante a captura.');
        }
        if (!result.startsWith('data:image/jpeg;base64,')) {
          throw new Error('A captura da marcação retornou um formato inválido.');
        }
        return result;
      } finally {
        captureLockRef.current = false;
        setCapturing(false);
      }
    },
  }), [aspectRatio, imageReady, imageFailed, imageSize, points.length, sourceGeneration, sourceUri, stageHeight, stageWidth]);

  const markDiameter = stageWidth < 400 ? 22 : 28;
  const constrainWebLandscape = Platform.OS === 'web' && windowWidth >= 1024 && windowWidth > windowHeight;

  const handleImageLoad = useCallback((event: any) => {
    if (!sourceUri || !acceptImageCallback(sourceUri, sourceGeneration, 'onLoad')) return;
    retainedPreview.onLoad();
    const nativeEvent = event?.nativeEvent ?? event;
    const source = nativeEvent?.source ?? nativeEvent?.target ?? nativeEvent?.currentTarget ?? event?.currentTarget;
    const width = Number(source?.width ?? source?.naturalWidth ?? 0);
    const height = Number(source?.height ?? source?.naturalHeight ?? 0);
    if (width > 0 && height > 0) {
      applyImageSize(sourceUri, sourceGeneration, width, height);
    } else {
      resolveImageSize(sourceUri, sourceGeneration);
    }
  }, [sourceUri, sourceGeneration, acceptImageCallback, retainedPreview.onLoad, applyImageSize, resolveImageSize]);

  const handleImageError = useCallback(() => {
    if (!sourceUri || !acceptImageCallback(sourceUri, sourceGeneration, 'onError')) return;
    acceptedImageStateRef.current = { uri: sourceUri, generation: sourceGeneration, status: 'failed' };
    traceLoad('mark_image_error', sourceGeneration, { retained: retainedPreview.pending });
    setImageFailed(true);
    if (!retainedPreview.pending) setImageSize(null);
  }, [sourceUri, sourceGeneration, acceptImageCallback, traceLoad, retainedPreview.pending]);

  const GestureStage = View as React.ComponentType<any>;
  return <View style={styles.container}>
    <GestureStage
      ref={stageRef}
      collapsable={false}
      onLayout={handleLayout}
      {...(Platform.OS === 'web' ? {
        onPointerDown: startGesture,
        onPointerMove: moveGesture,
        onPointerUp: (event: any) => finishGesture(event),
        onPointerCancel: (event: any) => finishGesture(event, true),
        onPointerLeave: (event: any) => finishGesture(event, true),
      } : {
        onTouchStart: startGesture,
        onTouchMove: moveGesture,
        onTouchEnd: (event: any) => finishGesture(event),
        onTouchCancel: (event: any) => finishGesture(event, true),
      })}
      style={[styles.stage, { aspectRatio }, constrainWebLandscape && styles.desktopLandscapeStage]}
      accessibilityLabel="Imagem para adicionar marcações"
      accessibilityRole="image"
      accessibilityValue={{ min: 0, now: points.length, text: `${points.length} marcações novas não salvas` }}
      testID={`mark-pad-${fieldId}`}
    >
      {retainedPreview.pendingUri && <Image
        key={retainedPreview.visibleGeneration}
        source={{ uri: retainedPreview.visibleUri! }}
        style={StyleSheet.absoluteFill}
        resizeMode="stretch"
        testID={`mark-retained-${fieldId}`}
      />}
      {sourceUri ? <Image
        key={sourceGeneration}
        source={{ uri: sourceUri }}
        style={[StyleSheet.absoluteFill, retainedPreview.pending && { opacity: 0 }]}
        resizeMode="stretch"
        onLoad={handleImageLoad}
        onError={handleImageError}
      /> : <View style={styles.emptyStage}><Text style={styles.emptyText}>Imagem de marcação indisponível</Text></View>}
      {imageSize && points.map((point, index) => <View
        key={`${index}-${point.x}-${point.y}`}
        accessible
        testID={`mark-point-${fieldId}-${index}`}
        accessibilityValue={{ text: `${point.x},${point.y}` }}
        pointerEvents="none"
        style={[
          styles.mark,
          {
            width: markDiameter,
            height: markDiameter,
            borderRadius: markDiameter / 2,
            left: point.x * stageWidth - markDiameter / 2,
            top: point.y * stageHeight - markDiameter / 2,
          },
        ]}
      />)}
    </GestureStage>
    {sourceUri && (imageFailed
      ? <ChecklistImageLoadNotice uri={sourceUri} onRetry={retainedPreview.retry} testID={`mark-load-status-${fieldId}`} />
      : !imageReady && <View testID={`mark-load-status-${fieldId}`} style={styles.captureStatus}>
        <ActivityIndicator size="small" color={theme.colors.primary.default} />
        <Text style={styles.captureText}>Carregando imagem…</Text>
      </View>)}
    {capturing && <View style={styles.captureStatus}><ActivityIndicator size="small" color={theme.colors.primary.default} /><Text style={styles.captureText}>Preparando imagem…</Text></View>}
  </View>;
});

const styles = StyleSheet.create({
  container: { width: '100%' },
  stage: {
    width: '100%',
    overflow: 'hidden',
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.background.secondary,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  desktopLandscapeStage: {
    maxWidth: 840,
    alignSelf: 'center',
  },
  mark: {
    position: 'absolute',
    borderWidth: 3,
    borderColor: '#d32f2f',
    backgroundColor: 'rgba(211, 47, 47, 0.16)',
  },
  emptyStage: { flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 180 },
  emptyText: { color: theme.colors.text.secondary, fontSize: 13 },
  captureStatus: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8 },
  captureText: { fontSize: 12, color: theme.colors.text.secondary },
});

export default InlineMarkPad;
